import { useMemo, useState } from 'react';
import { usePermissions } from '@/hooks/usePermissions';
import { useAsyncAction } from '@/hooks/useAsyncAction';
import { useConfirm } from '@/hooks/useConfirm';
import { useNginxConfigs } from '@/hooks/data/useNginxConfigs';
import { useDomains } from '@/hooks/data/useDomains';
import { useNodes } from '@/hooks/data/useNodes';
import { Button } from '@/components/ui/Button';
import { Select } from '@/components/ui/Select';
import { PlusIcon, RefreshIcon } from '@/components/icons';
import {
  ClearFiltersButton,
  ErrorAlert,
  PageHeader,
  SearchField,
} from '@/components/PageParts';
import {
  NginxConfigsTable,
  type NginxConfigRowAction,
} from '@/components/nginx/NginxConfigsTable';
import { ViewConfigModal } from '@/components/nginx/ViewConfigModal';
import { GenerateConfigModal } from '@/components/nginx/GenerateConfigModal';
import { ReloadNginxModal } from '@/components/nginx/ReloadNginxModal';
import { reloadStatusMap } from '@/config/statusMaps';
import { domainRules, getNginxConfigActions } from '@/config/resourceRules';
import { pluralize } from '@/utils/format';
import type { NginxConfig, ReloadStatus } from '@/types/nginx';
import './Nginx.css';

type BusyRowAction = Extract<NginxConfigRowAction, 'activate' | 'regenerate'>;

const ACTIVE_FILTER_OPTIONS = [
  { value: 'true', label: 'Active Only' },
  { value: 'false', label: 'Inactive Only' },
] as const;

/**
 * NGINX configurations page component
 *
 * Features:
 * - List all nginx configurations with filtering
 * - View config content
 * - Generate, activate, regenerate, delete configs (operator+)
 * - Reload nginx on nodes (operator+)
 */
export function Nginx() {
  const { role, can } = usePermissions();

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [nodeFilter, setNodeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<ReloadStatus | ''>('');
  const [activeFilter, setActiveFilter] = useState<'true' | 'false' | ''>('');

  const {
    configs,
    isLoading,
    error: loadError,
    reload,
    generateConfig,
    reloadNginx,
    activateConfig,
    regenerateConfig,
    deleteConfig,
  } = useNginxConfigs({
    node_id: nodeFilter || undefined,
    last_reload_status: statusFilter || undefined,
    is_active: activeFilter ? activeFilter === 'true' : undefined,
  });

  // Lookups for names and modal options (failures are non-critical)
  const { nodes } = useNodes();
  const { domains } = useDomains();

  // UI state
  const [openModal, setOpenModal] = useState<'generate' | 'reload' | null>(null);
  const [viewedConfigId, setViewedConfigId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const { confirm, confirmDialog } = useConfirm();

  const rowAction = useAsyncAction(
    async (action: BusyRowAction, config: NginxConfig) => {
      if (action === 'activate') {
        await activateConfig(config.id);
      } else {
        await regenerateConfig(config.id);
      }
    },
    {
      errorMessage: (action) =>
        action === 'activate' ? 'Failed to activate config' : 'Failed to regenerate config',
      getKey: (action, config) => `${action}:${config.id}`,
      onError: setActionError,
    }
  );

  const nodeNames = useMemo(() => new Map(nodes.map((n) => [n.id, n.name])), [nodes]);
  const domainNames = useMemo(() => new Map(domains.map((d) => [d.id, d.name])), [domains]);
  const getNodeName = (nodeId: string | null) => (nodeId ? nodeNames.get(nodeId) ?? 'Unknown' : '-');
  const getDomainName = (domainId: string | null) =>
    domainId ? domainNames.get(domainId) ?? 'Unknown' : '-';

  /**
   * Filter configs by search query (node or domain name)
   */
  const query = searchQuery.trim().toLowerCase();
  const filteredConfigs = query
    ? configs.filter(
        (config) =>
          getNodeName(config.node_id).toLowerCase().includes(query) ||
          getDomainName(config.domain_id).toLowerCase().includes(query)
      )
    : configs;

  const eligibleDomains = domains.filter(domainRules.isAssigned);
  const onlineNodes = nodes.filter((n) => n.status === 'online');

  const handleRowAction = (action: NginxConfigRowAction, config: NginxConfig) => {
    setActionError(null);
    switch (action) {
      case 'view':
        setViewedConfigId(config.id);
        return;
      case 'delete':
        void confirm({
          title: 'Delete Configuration',
          message: (
            <>
              Are you sure you want to delete the NGINX configuration for{' '}
              <strong>{getDomainName(config.domain_id)}</strong>? This action cannot be undone.
            </>
          ),
          confirmLabel: 'Delete',
          onConfirm: () => deleteConfig(config.id),
          errorMessage: 'Failed to delete config',
        });
        return;
      default:
        void rowAction.run(action, config);
    }
  };

  const handleClearFilters = () => {
    setSearchQuery('');
    setNodeFilter('');
    setStatusFilter('');
    setActiveFilter('');
  };

  const handleRetry = () => {
    setActionError(null);
    void reload();
  };

  const hasActiveFilters = Boolean(searchQuery || nodeFilter || statusFilter || activeFilter);
  const canGenerate = can('nginx:generate');
  const canReload = can('nginx:reload');

  return (
    <div className="nginx-page">
      <PageHeader
        classPrefix="nginx"
        title="NGINX Configurations"
        count={pluralize(filteredConfigs.length, 'config')}
        wrapActions
        actions={
          (canReload || canGenerate) && (
            <>
              {canReload && (
                <Button variant="secondary" leftIcon={<RefreshIcon />} onClick={() => setOpenModal('reload')}>
                  Reload NGINX
                </Button>
              )}
              {canGenerate && (
                <Button leftIcon={<PlusIcon />} onClick={() => setOpenModal('generate')}>
                  Generate Config
                </Button>
              )}
            </>
          )
        }
      />

      {/* Filters */}
      <div className="nginx-filters">
        <SearchField
          classPrefix="nginx"
          placeholder="Search by node or domain..."
          value={searchQuery}
          onChange={setSearchQuery}
        />

        <Select
          value={nodeFilter}
          onChange={setNodeFilter}
          placeholder="All Nodes"
          options={nodes.map((node) => ({ value: node.id, label: node.name }))}
          className="nginx-filter-select"
          aria-label="Filter by node"
        />

        <Select
          value={statusFilter}
          onChange={setStatusFilter}
          placeholder="All Status"
          options={reloadStatusMap.options}
          className="nginx-filter-select"
          aria-label="Filter by reload status"
        />

        <Select
          value={activeFilter}
          onChange={setActiveFilter}
          placeholder="Active & Inactive"
          options={ACTIVE_FILTER_OPTIONS}
          className="nginx-filter-select"
          aria-label="Filter by active state"
        />

        {hasActiveFilters && <ClearFiltersButton classPrefix="nginx" onClick={handleClearFilters} />}
      </div>

      <ErrorAlert classPrefix="nginx" message={actionError ?? loadError} onRetry={handleRetry} />

      <NginxConfigsTable
        configs={filteredConfigs}
        isLoading={isLoading}
        hasActiveFilters={hasActiveFilters}
        onClearFilters={handleClearFilters}
        onGenerateFirst={canGenerate ? () => setOpenModal('generate') : undefined}
        getDomainName={getDomainName}
        getNodeName={getNodeName}
        getActions={(config) => getNginxConfigActions(role, config)}
        isBusy={(action, configId) => rowAction.isPending(`${action}:${configId}`)}
        onAction={handleRowAction}
      />

      <ViewConfigModal configId={viewedConfigId} onClose={() => setViewedConfigId(null)} />

      {openModal === 'generate' && (
        <GenerateConfigModal
          isOpen
          onClose={() => setOpenModal(null)}
          domains={eligibleDomains}
          onGenerate={generateConfig}
        />
      )}

      {openModal === 'reload' && (
        <ReloadNginxModal
          isOpen
          onClose={() => setOpenModal(null)}
          nodes={onlineNodes}
          onReload={reloadNginx}
        />
      )}

      {confirmDialog}
    </div>
  );
}
